import { useWorkspaceText } from '../interface-copy';
// A slide's chart, drawn to the same plot box, the same scale and the same
// ticks as the .pptx exporter (src/company-workspace/decks/pptx.mjs). The marks
// below mirror its columnMarks/barMarks/lineMarks/stackedMarks one for one;
// where they differ, the reader watches the chart move between the canvas and
// the download.
//
// The legend lives outside the SVG, in flow, exactly where the exporter places
// it above the plot — so the SVG's box is the content box less the legend band.
import type { DeckChart } from '../types';
import {
  CHART,
  CHART_CATEGORICAL,
  CHART_SEQUENTIAL,
  HORIZONTAL_TYPES,
  STACKED_TYPES,
  chartScale,
  formatValue,
  linePointX,
  ticksOf,
} from './frame';

type Box = { width: number; height: number };
type Plot = { x: number; y: number; width: number; height: number };
type Scale = ReturnType<typeof chartScale>;
type Format = { valueFormat?: string; unit?: string };
type Marks = {
  chart: DeckChart;
  plot: Plot;
  scale: Scale;
  colors: readonly string[];
  format: Format;
};

function ColumnMarks({ chart, plot, scale, colors }: Marks) {
  const step = plot.width / chart.categories.length;
  // Thin marks: with two or three categories a proportional column becomes a
  // slab, so the group is capped and centred in its band.
  const group = Math.min(step * 0.64, CHART.maxColumnWidth * chart.series.length);
  const bar = group / chart.series.length;
  const zero = plot.y + plot.height - ((0 - scale.min) / scale.span) * plot.height;
  return (
    <>
      {chart.series.flatMap((one, series) =>
        one.values.map((value, at) => {
          if (value === null || value === undefined) return null;
          const y = plot.y + plot.height - ((value - scale.min) / scale.span) * plot.height;
          return (
            <rect
              key={`${series}-${at}`}
              x={plot.x + at * step + (step - group) / 2 + series * bar}
              y={Math.min(y, zero)}
              // A 2px gap of surface keeps adjacent fills from reading as one mark.
              width={Math.max(1, bar - 2)}
              height={Math.max(2, Math.abs(zero - y))}
              fill={colors[series % colors.length]}
            />
          );
        }),
      )}
    </>
  );
}

// A horizontal bar carries its value at the data end rather than against a
// value axis. For a handful of bars that reads more easily than a gridded
// axis, and it is the difference between a slide you can quote and one you can
// only rank: this chart shipped once with no number on it anywhere.
function BarMarks({ chart, plot, scale, colors, format }: Marks) {
  const step = plot.height / chart.categories.length;
  const group = Math.min(step * 0.62, CHART.maxBarThickness * chart.series.length);
  const bar = group / chart.series.length;
  return (
    <>
      {chart.series.flatMap((one, series) =>
        one.values.map((value, at) => {
          if (value === null || value === undefined) return null;
          const width = Math.max(2, ((value - scale.min) / scale.span) * plot.width);
          const y = plot.y + at * step + (step - group) / 2 + series * bar;
          return (
            <g key={`${series}-${at}`}>
              <rect
                x={plot.x}
                y={y}
                width={width}
                height={Math.max(1, bar - 2)}
                fill={colors[series % colors.length]}
              />
              <text className="deck-chart-label" x={plot.x + width + 10} y={y + bar / 2 + 1}>
                {formatValue(value, format)}
              </text>
            </g>
          );
        }),
      )}
    </>
  );
}

function StackedColumnMarks({ chart, plot, scale, colors }: Marks) {
  const step = plot.width / chart.categories.length;
  const width = Math.min(step * 0.6, CHART.maxColumnWidth);
  return (
    <>
      {chart.categories.flatMap((_, at) => {
        let base = 0;
        return chart.series.map((one, series) => {
          const value = one.values[at] ?? 0;
          if (value <= 0) return null;
          const top =
            plot.y + plot.height - ((base + value - scale.min) / scale.span) * plot.height;
          const bottom = plot.y + plot.height - ((base - scale.min) / scale.span) * plot.height;
          base += value;
          return (
            <rect
              key={`${series}-${at}`}
              x={plot.x + at * step + (step - width) / 2}
              y={top}
              width={width}
              height={Math.max(2, bottom - top - 2)}
              fill={colors[series % colors.length]}
            />
          );
        });
      })}
    </>
  );
}

function StackedBarMarks({ chart, plot, scale, colors }: Marks) {
  const step = plot.height / chart.categories.length;
  const height = Math.min(58, step * 0.5);
  return (
    <>
      {chart.categories.flatMap((_, at) => {
        let base = 0;
        return chart.series.map((one, series) => {
          const value = one.values[at] ?? 0;
          if (value <= 0) return null;
          const left = plot.x + ((base - scale.min) / scale.span) * plot.width;
          const right = plot.x + ((base + value - scale.min) / scale.span) * plot.width;
          base += value;
          return (
            <rect
              key={`${series}-${at}`}
              x={left}
              y={plot.y + at * step + (step - height) / 2}
              width={Math.max(2, right - left - 2)}
              height={height}
              fill={colors[series % colors.length]}
            />
          );
        });
      })}
    </>
  );
}

function LineMarks({ chart, plot, scale, colors }: Marks) {
  const at = (index: number, value: number) => ({
    x: linePointX(index, chart.categories.length, plot),
    y: plot.y + plot.height - ((value - scale.min) / scale.span) * plot.height,
  });
  return (
    <>
      {chart.series.map((one, series) => {
        const color = colors[series % colors.length];
        // A missing value breaks the line rather than being bridged: a segment
        // drawn straight through a gap asserts a measurement nobody took.
        const runs: { x: number; y: number }[][] = [[]];
        one.values.forEach((value, index) => {
          if (value === null || value === undefined) runs.push([]);
          else runs[runs.length - 1].push(at(index, value));
        });
        return (
          <g key={series}>
            {runs
              .filter((run) => run.length > 1)
              .map((run, index) => (
                <polyline
                  key={index}
                  points={run.map((point) => `${point.x},${point.y}`).join(' ')}
                  fill="none"
                  stroke={color}
                  strokeWidth={2}
                  strokeLinejoin="round"
                />
              ))}
            {runs.flat().map((point, index) => (
              <circle
                key={index}
                cx={point.x}
                cy={point.y}
                r={4.5}
                fill={color}
                stroke="var(--deck-paper)"
                strokeWidth={1.5}
              />
            ))}
          </g>
        );
      })}
    </>
  );
}

const MARKS = {
  column: ColumnMarks,
  bar: BarMarks,
  line: LineMarks,
  'stacked-column': StackedColumnMarks,
  'stacked-bar': StackedBarMarks,
};

export function DeckLegend({ chart }: { chart: DeckChart }) {
  const colors = STACKED_TYPES.includes(chart.type) ? CHART_SEQUENTIAL : CHART_CATEGORICAL;
  return (
    <div className="deck-legend">
      {chart.series.map((one, at) => (
        <span className="deck-legend-entry" key={one.name}>
          <span
            className="deck-legend-swatch"
            style={{ background: colors[at % colors.length] }}
            aria-hidden="true"
          />
          <span className="deck-legend-label">{one.name}</span>
        </span>
      ))}
    </div>
  );
}

export function DeckChartPlot({ chart, box }: { chart: DeckChart; box: Box }) {
  const { text: uiText, rich: uiRich } = useWorkspaceText();
  const horizontal = HORIZONTAL_TYPES.includes(chart.type);
  const gutter = horizontal ? CHART.categoryGutter : CHART.valueGutter;
  const plot = {
    x: gutter,
    y: CHART.axisHeadroom,
    width: box.width - gutter - (horizontal ? CHART.barLabelGutter : 0),
    height: box.height - CHART.axisHeadroom - CHART.categoryBand,
  };
  const scale = chartScale(chart);
  const colors = STACKED_TYPES.includes(chart.type) ? CHART_SEQUENTIAL : CHART_CATEGORICAL;
  const format = { valueFormat: chart.valueFormat, unit: chart.unit };
  const Marks = MARKS[chart.type];
  const band = (horizontal ? plot.height : plot.width) / chart.categories.length;
  return (
    <svg
      viewBox={`0 0 ${box.width} ${box.height}`}
      role="img"
      aria-label={uiText('{value1} chart of {value2} across {value3}', {
        value1: chart.type,
        value2: chart.series.map((one) => one.name).join(', '),
        value3: chart.categories.join(', '),
      })}
    >
      {horizontal ? (
        <>
          {chart.categories.map((category, at) => (
            <text
              className="deck-chart-label"
              key={category}
              x={gutter - 16}
              y={plot.y + at * band + band / 2 + 4}
              textAnchor="end"
            >
              {category}
            </text>
          ))}
          <line
            className="deck-axis-line"
            x1={plot.x}
            x2={plot.x + plot.width}
            y1={plot.y + plot.height}
            y2={plot.y + plot.height}
          />
        </>
      ) : (
        <>
          {ticksOf(scale).map((value) => {
            const y = plot.y + plot.height - ((value - scale.min) / scale.span) * plot.height;
            return (
              <g key={value}>
                <line
                  className="deck-grid-line"
                  x1={plot.x}
                  x2={plot.x + plot.width}
                  y1={y}
                  y2={y}
                />
                <text className="deck-chart-label" x={plot.x - 16} y={y + 4} textAnchor="end">
                  {formatValue(value, format)}
                </text>
              </g>
            );
          })}
          {/* Bands for a column, points for a line: a column belongs to a slice
              of the axis, a line's value to an instant on it, so the labels
              follow the marks. */}
          {chart.categories.map((category, at) => {
            const line = chart.type === 'line';
            const x = line
              ? linePointX(at, chart.categories.length, plot)
              : plot.x + at * band + band / 2;
            const edge = line && (at === 0 || at === chart.categories.length - 1);
            return (
              <text
                className="deck-chart-label"
                key={category}
                x={x}
                y={plot.y + plot.height + 24}
                textAnchor={edge ? (at === 0 ? 'start' : 'end') : 'middle'}
              >
                {category}
              </text>
            );
          })}
        </>
      )}
      <Marks chart={chart} plot={plot} scale={scale} colors={colors} format={format} />
    </svg>
  );
}
